import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import api from '../api';
import Spinner from './Spinner';
import ProductLogo from './ProductLogo';

const navigateToProduct = url => window.location.assign(url);

export default function ProductsSidebar({ onNavigate = navigateToProduct }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [error, setError] = useState('');
    const [launching, setLaunching] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const busy = useRef(false);

    const load = useCallback(() => {
        setLoading(true);
        setLoadError(false);
        api.get('/api/products').then(({ data }) => setProducts(data))
            .catch(() => setLoadError(true)).finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const launch = async product => {
        if (busy.current) return;
        busy.current = true;
        setLaunching(product.id);
        setError('');
        try {
            const { data } = await api.post(`/api/products/${product.id}/launch`);
            onNavigate(data.url);
        } catch (e) {
            setError(e.response?.status === 401
                ? 'Сессия истекла. Обновите страницу и войдите снова.'
                : 'Не удалось открыть продукт. Попробуйте ещё раз.');
            busy.current = false;
            setLaunching(null);
        }
    };

    return (
        <aside aria-label="Наши продукты" className="order-2 lg:order-3 w-full lg:w-[320px] xl:w-[336px] shrink-0 bg-white border-b lg:border-b-0 lg:border-l border-gray-200 lg:overflow-y-auto">
            <div className="px-6 py-5 lg:pt-10 lg:pb-6">
                <h2 className="hidden lg:block text-xl font-semibold text-gray-900 m-0">Наши продукты</h2>
                <button type="button" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-controls="product-list"
                        className="lg:hidden flex items-center justify-between w-full text-lg font-semibold text-gray-900 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
                    Наши продукты {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                <p className="hidden lg:block text-sm leading-relaxed text-gray-500 mt-2 mb-0">Открывайте сервисы со своим аккаунтом IT-Burger.</p>
            </div>
            <div id="product-list" className={`${expanded ? 'block' : 'hidden'} lg:block px-3 pb-6`}>
                {loading ? <div className="px-3 py-5 text-gray-500 flex items-center gap-2 text-sm"><Spinner /> Загружаем продукты…</div>
                    : loadError ? <div className="px-3 text-sm text-gray-600"><p role="alert">Не удалось загрузить продукты.</p><button type="button" onClick={load} className="text-blue-600 underline">Попробовать снова</button></div>
                    : products.length === 0 ? <p className="px-3 text-sm text-gray-500">Продукты пока не добавлены.</p>
                    : <ul className="m-0 p-0 list-none divide-y divide-gray-100">
                        {products.map(product => (
                            <li key={product.id} className="py-1">
                                <button type="button" onClick={() => launch(product)} disabled={launching !== null} aria-busy={launching === product.id}
                                        className="group flex items-start gap-3 w-full p-3 rounded-xl text-left transition-colors hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:cursor-wait disabled:opacity-60">
                                    <ProductLogo src={product.logoUrl} title={product.title} />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-base font-semibold text-gray-900 group-hover:text-blue-700 break-words">{product.title}</span>
                                        <span className="block mt-1 text-sm leading-relaxed text-gray-500 break-words">{product.description}</span>
                                        {launching === product.id && <span className="flex items-center gap-2 mt-2 text-xs text-blue-600"><Spinner size={13} /> Открываем…</span>}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>}
                {error && <p role="alert" className="mx-3 text-sm text-red-700">{error}</p>}
            </div>
        </aside>
    );
}
